import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { IoClose, IoCamera, IoWarning } from "react-icons/io5";
import { useCouponSmart, Coupon } from "../../hooks/useCoupons";

const CartQRPopup = ({
  open,
  onClose,
  onResult,
}: {
  open: boolean;
  onClose: () => void;
  onResult: (matched: Coupon | null, code: string) => void;
}) => {
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const cameraConfigRef = useRef<any>(null);
  const scannerElementRef = useRef<HTMLDivElement>(null);
  const [scannerState, setScannerState] = useState<
    "loading" | "ready" | "error" | "no-camera"
  >("loading");
  const [scannedCode, setScannedCode] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [hasScanned, setHasScanned] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const {
    data: couponData,
    isSuccess,
    isError,
    error,
  } = useCouponSmart(scannedCode);

  // تحديد إذا كان الجهاز موبايل
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const mobileRegex =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      setIsMobile(mobileRegex.test(userAgent) || window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // 🔥 إيقاف الكاميرا فوراً عند إغلاق الـ popup
  useEffect(() => {
    if (!open && html5QrCodeRef.current) {
      const qrInstance = html5QrCodeRef.current;
      html5QrCodeRef.current = null;

      try {
        const state = qrInstance.getState();
        if (
          state === Html5QrcodeScannerState.SCANNING ||
          state === Html5QrcodeScannerState.PAUSED
        ) {
          qrInstance
            .stop()
            .then(() => {
              qrInstance.clear();
            })
            .catch(() => {
              qrInstance.clear();
            });
        } else {
          qrInstance.clear();
        }
      } catch (error) {
        try {
          qrInstance.clear();
        } catch (clearError) {}
      }

      // إعادة تعيين الحالة
      setScannerState("loading");
      setScannedCode("");
      setErrorMessage("");
      setHasScanned(false);
    }
  }, [open]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && html5QrCodeRef.current) {
        stopAndClose();
      }
    };

    const handleBeforeUnload = () => {
      if (html5QrCodeRef.current) {
        const qrInstance = html5QrCodeRef.current;
        try {
          const state = qrInstance.getState();
          if (
            state === Html5QrcodeScannerState.SCANNING ||
            state === Html5QrcodeScannerState.PAUSED
          ) {
            qrInstance.stop().catch(() => {});
            qrInstance.clear();
          }
        } catch (error) {}
        html5QrCodeRef.current = null;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handleBeforeUnload);
    };
  }, []);

  // تحسين viewport للموبايل
  useEffect(() => {
    if (isMobile && open) {
      // إضافة أو تحديث viewport meta tag للموبايل
      let viewportMeta = document.querySelector(
        'meta[name="viewport"]'
      ) as HTMLMetaElement;
      if (!viewportMeta) {
        viewportMeta = document.createElement("meta");
        viewportMeta.name = "viewport";
        document.head.appendChild(viewportMeta);
      }
      const originalContent = viewportMeta.content;
      viewportMeta.content =
        "width=device-width, initial-scale=1.0, user-scalable=no";

      // معالجة تغيير اتجاه الشاشة للموبايل
      const handleOrientationChange = () => {
        if (html5QrCodeRef.current && scannerState === "ready") {
          // إعادة تشغيل المسح بعد تغيير الاتجاه
          setTimeout(() => {
            setScannerState("loading");
            setTimeout(() => setScannerState("ready"), 500);
          }, 100);
        }
      };

      window.addEventListener("orientationchange", handleOrientationChange);

      // استعادة القيمة الأصلية عند الإغلاق
      return () => {
        if (originalContent) {
          viewportMeta.content = originalContent;
        }
        window.removeEventListener(
          "orientationchange",
          handleOrientationChange
        );
      };
    }
  }, [isMobile, open, scannerState]);

  // 🔥 تحسين إيقاف الكاميرا مع المزيد من القوة
  const stopAndClose = () => {
    const qrInstance = html5QrCodeRef.current;
    setHasScanned(true);

    if (qrInstance) {
      html5QrCodeRef.current = null; // إزالة المرجع فوراً لمنع استخدامه مرة أخرى

      try {
        const state = qrInstance.getState();

        if (
          state === Html5QrcodeScannerState.SCANNING ||
          state === Html5QrcodeScannerState.PAUSED
        ) {
          qrInstance
            .stop()
            .then(() => {
              qrInstance.clear();
            })
            .catch(() => {
              qrInstance.clear();
            })
            .finally(() => {
              // إيقاف أي مسارات فيديو متبقية كحل أخير
              const vids = document.querySelectorAll("video");
              vids.forEach((v) => {
                const stream = (v as HTMLVideoElement)
                  .srcObject as MediaStream | null;
                if (stream) {
                  stream.getTracks().forEach((t) => t.stop());
                }
              });

              onClose();
              setScannedCode("");
            });
        } else {
          qrInstance.clear();
          onClose();
          setScannedCode("");
        }
      } catch (error) {
        try {
          qrInstance.clear();
        } catch (clearError) {}
        onClose();
        setScannedCode("");
      }
    } else {
      onClose();
      setScannedCode("");
    }
  };

  useEffect(() => {
    if (!open || scannedCode === "") return;

    if (isSuccess) {
      const matched = couponData;

      // التحقق الشامل من صلاحية الكوبون (نفس logic الإدخال اليدوي)
      if (!matched) {
        onResult(null, scannedCode);
        stopAndClose();
        return;
      }

      // 🧪 وضع اختبار مؤقت (يمكن إزالته لاحقاً)
      const isTestMode =
        process.env.NODE_ENV === "development" ||
        window.location.hostname === "localhost" ||
        window.location.hostname.includes("192.168") ||
        localStorage.getItem("testMode") === "true";

      // فحص نشاط الكوبون
      if (!matched.is_active && !isTestMode) {
        onResult(null, scannedCode);
        stopAndClose();
        return;
      }

      // فحص توفر الكوبون
      if (!matched.is_available && !isTestMode) {
        onResult(null, scannedCode);
        stopAndClose();
        return;
      }

      // فحص صلاحية التاريخ
      const today = new Date();
      const validFrom = new Date(matched.valid_from);
      const validTo = new Date(matched.valid_to);

      if (today < validFrom && !isTestMode) {
        onResult(null, scannedCode);
        stopAndClose();
        return;
      }

      if (today > validTo) {
        onResult(null, scannedCode);
        stopAndClose();
        return;
      }

      // ✅ الكوبون صالح تماماً

      onResult(matched, scannedCode);
      stopAndClose();
    } else if (isError) {
      onResult(null, scannedCode);
      stopAndClose();
    } else {
    }
  }, [
    isSuccess,
    isError,
    couponData,
    error,
    open,
    scannedCode,
    onResult,
    onClose,
  ]);

  // 🔥 تحسين اختيار الكاميرا الخلفية للموبايل
  useEffect(() => {
    if (!open) return;

    setHasScanned(false);
    setScannerState("loading");
    setScannedCode("");
    setErrorMessage("");

    // للموبايل: البدء مباشرة بالكاميرا الخلفية
    if (isMobile) {
      cameraConfigRef.current = { facingMode: { exact: "environment" } };
      setScannerState("ready");
      return;
    }

    // للديسكتوب: طلب إذن الكاميرا أولاً ثم اختيار الأنسب
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      const constraints = { video: true };

      navigator.mediaDevices
        .getUserMedia(constraints)
        .then(() => {
          // بعد الحصول على الإذن، احصل على قائمة الكاميرات
          return Html5Qrcode.getCameras();
        })
        .then((cameras) => {
          if (cameras.length === 0) {
            setScannerState("no-camera");
            setErrorMessage("لا توجد كاميرا متاحة على هذا الجهاز");
            return;
          }

          // للديسكتوب: ابحث عن الكاميرا الخلفية
          let selectedCamera = cameras.find((cam) =>
            cam.label.toLowerCase().includes("back")
          );

          // إذا لم توجد كاميرا خلفية، استخدم الأولى المتاحة
          if (!selectedCamera) {
            selectedCamera = cameras[0];
          }

          // إعداد الكاميرا
          if (selectedCamera) {
            cameraConfigRef.current = {
              deviceId: { exact: selectedCamera.id },
            };
          } else {
            cameraConfigRef.current = { facingMode: "environment" };
          }

          setScannerState("ready");
        })
        .catch((err) => {
          console.error("Error in QR Scanner Prepare:", err);
          setScannerState("error");
          setErrorMessage(
            err instanceof Error
              ? `خطأ في الكاميرا: ${err.message}`
              : "حدث خطأ في تشغيل الكاميرا"
          );
        });
    } else {
      setScannerState("error");
      setErrorMessage("متصفحك لا يدعم استخدام الكاميرا");
    }

    // 🔥 تحسين cleanup function بشكل أكثر إجباراً
    return () => {
      if (html5QrCodeRef.current) {
        const qrInstance = html5QrCodeRef.current;
        html5QrCodeRef.current = null;

        try {
          const state = qrInstance.getState();
          if (
            state === Html5QrcodeScannerState.SCANNING ||
            state === Html5QrcodeScannerState.PAUSED
          ) {
            // إيقاف synchronous بدون انتظار
            qrInstance.stop().finally(() => {
              qrInstance.clear();
            });
          } else {
            qrInstance.clear();
          }
        } catch (error) {
          try {
            qrInstance.clear();
          } catch (clearError) {}
        }
      }
    };
  }, [open, isMobile]);

  useEffect(() => {
    if (scannerState !== "ready" || !open || hasScanned) return;

    const startScanner = async () => {
      try {
        // إيقاف المسح السابق إن وجد
        if (html5QrCodeRef.current) {
          const state = html5QrCodeRef.current.getState();
          if (
            state === Html5QrcodeScannerState.SCANNING ||
            state === Html5QrcodeScannerState.PAUSED
          ) {
            await html5QrCodeRef.current.stop();
            html5QrCodeRef.current.clear();
          }
        }

        const html5QrCode = new Html5Qrcode("popup-qr-scanner");
        html5QrCodeRef.current = html5QrCode;

        // 🔥 إعدادات محسنة مع التركيز على الكاميرا الخلفية للموبايل
        const qrConfig = {
          fps: isMobile ? 8 : 10, // fps معقول للموبايل
          qrbox: isMobile
            ? { width: 200, height: 200 } // حجم مناسب للموبايل
            : { width: 250, height: 250 },
          aspectRatio: 1.0,
          disableFlip: false,
          videoConstraints: isMobile
            ? {
                // إعدادات محسنة للموبايل مع التركيز على الكاميرا الخلفية
                width: { ideal: 640, max: 1280 },
                height: { ideal: 640, max: 1280 },
                facingMode: { exact: "environment" }, // 🔥 إجبار الكاميرا الخلفية
              }
            : {
                // إعدادات الديسكتوب
                width: { ideal: 640 },
                height: { ideal: 640 },
                facingMode: "environment",
              },
        };

        await html5QrCode.start(
          cameraConfigRef.current,
          qrConfig,
          (decodedText) => {
            if (decodedText === scannedCode || hasScanned) return;

            // 🔥 تنظيف شامل لمحتوى QR Code
            let cleanedText = decodedText
              .trim() // إزالة المسافات من البداية والنهاية
              .replace(/\s+/g, " ") // تحويل المسافات المتعددة إلى مسافة واحدة
              .replace(/[^\x20-\x7E\u00A0-\u024F\u0600-\u06FF]/g, "") // إزالة الأحرف الخفية والغريبة
              .trim(); // تنظيف نهائي

            // إذا كان النص فارغاً بعد التنظيف، استخدم الأصلي
            const finalText = cleanedText || decodedText.trim();

            // إضافة haptic feedback للموبايل
            if (isMobile && "vibrate" in navigator) {
              navigator.vibrate(100); // اهتزاز خفيف لمدة 100ms
            }

            setScannedCode(finalText);
            setHasScanned(true);

            // إيقاف المسح فوراً بعد النجاح
            html5QrCode
              .stop()
              .then(() => {
                html5QrCode.clear();
              })
              .catch(console.warn);
          },
          (error) => {
            // تجاهل الأخطاء العادية في المسح
            if (
              !error.includes("NotFoundException") &&
              !error.includes("No MultiFormat Readers") &&
              !error.includes("QR code parse error")
            ) {
            }
          }
        );
      } catch (err) {
        setScannerState("error");

        // رسائل خطأ مخصصة
        let customMessage = "فشل في تشغيل الكاميرا";
        if (err instanceof Error) {
          if (
            err.message.includes("OverconstrainedError") ||
            err.message.includes("NotReadableError")
          ) {
            customMessage = isMobile
              ? "لا يمكن الوصول للكاميرا الخلفية. يرجى التأكد من عدم استخدامها في تطبيق آخر"
              : "الكاميرا مستخدمة في تطبيق آخر";
          } else if (err.message.includes("NotAllowedError")) {
            customMessage = "يرجى السماح للتطبيق بالوصول للكاميرا";
          }
        }

        setErrorMessage(customMessage);
      }
    };

    const timeoutId = setTimeout(startScanner, isMobile ? 300 : 200); // تقليل الوقت للموبايل

    return () => clearTimeout(timeoutId);
  }, [scannerState, open, hasScanned, scannedCode, isMobile]);

  if (!open) return null;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4'>
      <div
        className={`bg-white rounded-2xl p-4 ${
          isMobile ? "w-full max-w-xs" : "w-full max-w-sm"
        } mx-auto shadow-xl relative`}
      >
        <button
          onClick={stopAndClose}
          className='absolute top-3 right-3 text-gray-500 hover:text-red-500 text-2xl z-10'
        >
          <IoClose />
        </button>

        <h2
          className={`text-gray-800 font-bold ${
            isMobile ? "text-lg" : "text-xl"
          } mb-4 text-center`}
        >
          مسح QR Code
        </h2>

        <div
          className={`w-full aspect-square mx-auto overflow-hidden rounded-xl border-2 border-gray-200 bg-gray-100 relative ${
            isMobile ? "max-w-[280px]" : ""
          }`}
        >
          {scannerState === "loading" && (
            <div className='flex flex-col items-center justify-center h-full'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mb-4'></div>
              <p className={`text-gray-600 ${isMobile ? "text-sm" : ""}`}>
                {isMobile
                  ? "جاري تشغيل الكاميرا الخلفية..."
                  : "جاري تشغيل الكاميرا..."}
              </p>
            </div>
          )}

          {scannerState === "error" && (
            <div className='flex flex-col items-center justify-center h-full p-4 text-center'>
              <IoWarning className='text-red-500 text-4xl mb-4' />
              <p className={`text-red-600 ${isMobile ? "text-xs" : "text-sm"}`}>
                {errorMessage}
              </p>
              <button
                onClick={() => {
                  setScannerState("loading");
                  setErrorMessage("");

                  // إعادة المحاولة للموبايل بالكاميرا الخلفية مباشرة
                  if (isMobile) {
                    cameraConfigRef.current = {
                      facingMode: { exact: "environment" },
                    };
                    setScannerState("ready");
                  } else {
                    // للديسكتوب: إعادة المحاولة مع طلب الإذن
                    if (
                      navigator.mediaDevices &&
                      navigator.mediaDevices.getUserMedia
                    ) {
                      navigator.mediaDevices
                        .getUserMedia({ video: true })
                        .then(() => Html5Qrcode.getCameras())
                        .then(() => setScannerState("ready"))
                        .catch(() => setScannerState("error"));
                    }
                  }
                }}
                className={`mt-4 bg-red-600 text-white px-6 py-3 rounded-lg ${
                  isMobile ? "text-sm w-full" : "text-sm"
                } hover:bg-red-700 transition-colors font-medium`}
              >
                {isMobile ? "إعادة تشغيل الكاميرا الخلفية" : "إعادة المحاولة"}
              </button>
            </div>
          )}

          {scannerState === "no-camera" && (
            <div className='flex flex-col items-center justify-center h-full p-4 text-center'>
              <IoCamera className='text-gray-400 text-4xl mb-4' />
              <p
                className={`text-gray-600 ${isMobile ? "text-xs" : "text-sm"}`}
              >
                {errorMessage}
              </p>
            </div>
          )}

          {scannerState === "ready" && (
            <div
              id='popup-qr-scanner'
              ref={scannerElementRef}
              className='absolute inset-0'
              style={{
                // ضمان responsive behavior
                width: "100%",
                height: "100%",
              }}
            />
          )}
        </div>

        {scannerState === "ready" && (
          <div className='mt-4 text-center'>
            <p
              className={`${
                isMobile ? "text-xs" : "text-sm"
              } text-gray-600 mb-2`}
            >
              {isMobile ? "ضع رمز QR في الإطار" : "ضع رمز QR داخل الإطار للمسح"}
            </p>
            <div className='flex items-center justify-center gap-2'>
              <div className='w-2 h-2 bg-green-500 rounded-full animate-pulse'></div>
              <span
                className={`${isMobile ? "text-xs" : "text-xs"} text-green-600`}
              >
                {isMobile ? "الكاميرا الخلفية نشطة" : "الكاميرا نشطة"}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartQRPopup;
